import React from 'react';
import { ARTIFACT_ID } from 'domain/shared/artifact';
import { ArtifactsListLayout, ImageLayout, DocumentLayout, AttachmentsListField } from 'domain/shared/artifact/field';
import { EmailAttachmentUploaderInput } from 'domain/shared/artifact/input';
import { useAccountUrl } from 'domain/shared/account';
import { useCandidateUrl } from 'domain/shared/candidate';
import { useEmployeeNamesService, useEmployeeUrl } from 'domain/shared/employee';
import { useProjectUrl } from 'domain/shared/project';
import { useLeadUrl } from 'domain/shared/lead';
import { useOpportunityUrl } from 'domain/sales/opportunity/opportunityRouting';
import { useContactUrl } from 'domain/sales/contact/contactRouting';
import { useEmployeeOfferUrl } from 'domain/people/employee/offers/employeeOfferRouting';
import { useCandidateOfferUrl } from 'domain/recruiting/candidate/offercompensation/CandidateOfferRouter';
import { useRecruitingFinanceService } from 'domain/shared/recruitingFinance';
import { useDateTimeService } from 'framework/utils';
import { DocumentUiConfigurationProvider } from 'infrastructure/documentUiConfiguration';
import { CustomHtmlTagHelperDependenciesProvider } from 'infrastructure/textTransformer/customHtmlTagsHelper/dependencies';
import { PostAuthenticationRedirectProvider } from 'infrastructure/security/authentication/postAuthenticationRedirect';
import { CustomForm, useOneStopRequestsUrl } from 'domain/shared/oneStop';
import { APPLICATION_URL } from 'infrastructure/constants';
import { useServiceUrl } from 'domain/shared/service';
import { CustomRequestFormMapContext } from 'domain/gpo/oneStop/CustomRequestForm/CustomRequestFormMapContext';
import { CustomRequestFormType } from 'domain/gpo/oneStop/CustomRequestForm/types';
import OneStopCareerDevelopmentReview from 'domain/people/career/development/createupdate/OneStop';
import OneStopHiringForm from 'domain/recruiting/hiring/createupdate/OneStop';
import OneStopCreateVacancy from 'domain/recruiting/newVacancy/createupdate/create/OneStopCreateVacancy';
import OneStopCreateVendorPage from 'domain/finance/vendors/createupdate/Create/OneStopCreateVendorPage';
import OneStopPurchaseRequest from 'domain/gpo/oneStop/purchaseRequest';
import OneStopWorkloadReportingIssues from 'domain/gpo/oneStop/workloadReportingIssues';
import OneStopBudgetReallocation from 'domain/finance/budgetReallocation/createupdate/OneStop';
import OneStopLeave from 'domain/people/leave/createupdate/OneStop';

const InfrastructureProviders = ({ children }: { children: React.ReactNode }) => {
  const { getSingleEntityUrl: getEmployeeUrl } = useEmployeeUrl();
  const { getSingleEntityUrl: getCandidateUrl } = useCandidateUrl();
  const { getSingleEntityUrl: getLeadUrl } = useLeadUrl();
  const { getTimelineUrl: getOpportunityUrl } = useOpportunityUrl();
  const { getSingleEntityUrl: getAccountUrl } = useAccountUrl();
  const { getSingleEntityUrl: getContactUrl } = useContactUrl();
  const { getSingleEntityUrl: getEmployeeOfferUrl } = useEmployeeOfferUrl();
  const { getSingleEntityUrl: getCandidateOfferUrl } = useCandidateOfferUrl();
  const { getSingleEntityUrl: getProjectUrl } = useProjectUrl();
  const { getSingleEntityUrl: getCompanyServiceUrl } = useServiceUrl();
  const { searchSensitiveRecruitingInfo } = useRecruitingFinanceService();
  const { formatDateWithTimezone, getUserTimezone } = useDateTimeService();
  const { getEmployeeNameDataById } = useEmployeeNamesService();
  const { getListUrl } = useOneStopRequestsUrl();
  const documentUiConfiguration = React.useMemo(
    () => ({
      ArtifactsListLayout,
      ArtifactImageLayout: ImageLayout,
      ArtifactDocumentLayout: DocumentLayout,
      EmailAttachmentUploaderInput,
      AttachmentsListField,
      emailAttachmentArtifactTypeId: ARTIFACT_ID.EMAIL_ATTACHMENT,
    }),
    [],
  );
  const customHtmlTagHelperDependencies = React.useMemo(
    () => ({
      getEmployeeUrl,
      getCandidateUrl,
      getLeadUrl,
      getOpportunityUrl,
      getAccountUrl,
      getContactUrl,
      getEmployeeOfferUrl,
      getCandidateOfferUrl,
      getProjectUrl,
      getCompanyServiceUrl,
      searchSensitiveRecruitingInfo,
      formatDateWithTimezone,
      getUserTimezone,
      getEmployeeNameDataById,
    }),
    [
      formatDateWithTimezone,
      getAccountUrl,
      getCandidateOfferUrl,
      getCandidateUrl,
      getCompanyServiceUrl,
      getContactUrl,
      getEmployeeNameDataById,
      getEmployeeOfferUrl,
      getEmployeeUrl,
      getLeadUrl,
      getOpportunityUrl,
      getProjectUrl,
      getUserTimezone,
      searchSensitiveRecruitingInfo,
    ],
  );
  const postAuthenticationRedirectValue = React.useMemo(
    () => ({
      successUrl: getListUrl(),
      errorUrl: APPLICATION_URL || '/',
    }),
    [getListUrl],
  );
  const formMap = React.useMemo<Dictionary<CustomRequestFormType>>(
    () => ({
      [CustomForm.CREATE_LEAVE]: OneStopLeave,
      [CustomForm.CREATE_HIRING_REQUEST_VACANCY]: OneStopHiringForm,
      [CustomForm.NEW_REQUEST_VACANCY]: OneStopCreateVacancy,
      [CustomForm.CREATE_CAREER_DEVELOPMENT_REVIEW]: OneStopCareerDevelopmentReview,
      [CustomForm.CREATE_BUDGET_REALLOCATION]: OneStopBudgetReallocation,
      [CustomForm.CREATE_VENDOR]: OneStopCreateVendorPage,
      [CustomForm.PURCHASE_REQUEST]: OneStopPurchaseRequest,
      [CustomForm.WORKLOAD_REPORTING_ISSUES]: OneStopWorkloadReportingIssues,
    }),
    [],
  );

  return (
    <CustomRequestFormMapContext.Provider value={formMap}>
      <DocumentUiConfigurationProvider value={documentUiConfiguration}>
        <CustomHtmlTagHelperDependenciesProvider value={customHtmlTagHelperDependencies}>
          <PostAuthenticationRedirectProvider value={postAuthenticationRedirectValue}>
            {children}
          </PostAuthenticationRedirectProvider>
        </CustomHtmlTagHelperDependenciesProvider>
      </DocumentUiConfigurationProvider>
    </CustomRequestFormMapContext.Provider>
  );
};

export default InfrastructureProviders;
